import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  TextField,
  createFilterOptions,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Controller, useFormContext } from "react-hook-form";
import { Fragment, useCallback } from "react";
import { debounce } from "shared/helpers";
import useTranslation from "shared/helpers/hooks/useTranslation";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const select_all = 0;

const selectAllOption = {
  id: select_all,
  name: "Select All",
  value: "Select All",
};

interface IAutocompleteProps<OptionType> {
  optionLabel?: keyof OptionType;
  inputLabel: string;
  async?: boolean;
  disabled?: boolean;
  loading?: boolean;
  size?: "small" | "medium";
  prefix?: string;
  onChangeCB?: (val: OptionType | OptionType[] | null) => void;
  fetchFn?: (search: string) => void;
  options: OptionType[];
  name: string;
  defaultValue: [] | "";
  multiple?: boolean;
  rules?: any;
  getOptionDisabled?: (option: OptionType) => boolean;
  groupBy?: (option: OptionType) => string;
  hasSelectAllOption?: boolean;
  isEqualByID?: boolean;
  isOptionEqualToValue?: (option: OptionType, value: any) => boolean;
  onFocus?: () => void;
  sx?: any;
  hasTranslatedOptions?: boolean;
  hasSeeMoreOption?: boolean;
  getMoreOptions?: () => void;
  placeholder?: string;
  customFilter?: (options: OptionType[], input: string) => OptionType[];
  generateCustomKey?: (option: OptionType) => string;
  generateOptionLabel?: (option: OptionType) => string;
}

const BasicAutocomplete = <T extends { id?: number | string }>({
  optionLabel,
  disabled,
  async = false,
  fetchFn,
  loading = false,
  onChangeCB,
  getOptionDisabled,
  inputLabel,
  options,
  size,
  name,
  prefix,
  rules,
  multiple = false,
  defaultValue,
  groupBy = undefined,
  hasSelectAllOption = false,
  isOptionEqualToValue = undefined,
  onFocus = undefined,
  sx,
  hasTranslatedOptions = false,
  hasSeeMoreOption = false,
  getMoreOptions = undefined,
  placeholder = "",
  customFilter = undefined,
  generateCustomKey = undefined,
  generateOptionLabel = undefined,
}: IAutocompleteProps<T>) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const allSelected = options?.length === watch(name)?.length;
  const filter = createFilterOptions();
  const t = useTranslation();

  const handleSelectAll = (isSelected) => {
    if (!isSelected) {
      setValue(name, options);
    } else {
      setValue(name, []);
    }
  };

  const getOptionLabel = (option: T) => {
    if (generateOptionLabel) {
      return generateOptionLabel(option);
    }
    if (option.id === -1) {
      return t("load_more");
    }
    if (typeof option === "string") {
      return hasTranslatedOptions ? t(option) : option;
    } else if (!optionLabel) {
      return "";
    }
    return hasTranslatedOptions
      ? t(option[optionLabel as string])
      : option[optionLabel as string] || "N/A";
  };

  const errorInfo = useCallback(() => {
    let errorData = errors as { [x: string]: any };
    if (prefix) {
      const nestedName = name.split(".")[1];
      return errorData?.[prefix]?.[nestedName];
    } else {
      return errorData?.[name];
    }
  }, [errors, name, prefix]);

  const handleLoadMore = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent closing the dropdown
    getMoreOptions?.();
  };

  const handleChange = (
    e: React.SyntheticEvent<Element, Event>,
    values: T | T[] | null
  ) => {
    if (multiple) {
      const isSelectAll = (values as T[]).find((i) => i.id === select_all);
      if (isSelectAll) {
        handleSelectAll(allSelected);
      } else {
        setValue(name, values, { shouldDirty: true });
        onChangeCB?.(values);
      }
    } else {
      if (hasSeeMoreOption && values?.id === -1) {
        return;
      } else {
        setValue(name, values, { shouldDirty: true });
        onChangeCB?.(values);
      }
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) => {
        return (
          <Autocomplete
            {...field}
            disabled={disabled}
            size={size}
            sx={sx}
            multiple={multiple}
            onChange={handleChange}
            groupBy={groupBy}
            limitTags={multiple ? 1 : undefined}
            options={[
              ...options,
              ...(hasSeeMoreOption ? [{ id: -1, name: "See more" }] : []),
            ]}
            onFocus={onFocus}
            filterOptions={(options, params) => {
              if (customFilter) {
                const input = params?.inputValue.toLowerCase();
                return customFilter(options, input);
              }
              const filtered = filter(options, params);
              return multiple && hasSelectAllOption && filtered.length
                ? [selectAllOption, ...filtered]
                : filtered;
            }}
            disableCloseOnSelect={multiple}
            isOptionEqualToValue={(option, value) => {
              if (isOptionEqualToValue) {
                return isOptionEqualToValue?.(option, value);
              }
              return JSON.stringify(option) === JSON.stringify(value);
            }}
            {...(async
              ? {
                  onInputChange: debounce((_, value) => fetchFn?.(value)),
                }
              : null)}
            getOptionLabel={getOptionLabel}
            getOptionDisabled={getOptionDisabled}
            loading={loading}
            renderOption={(props, option, { selected }) => {
              const selectAllProps =
                option.id === select_all ? { checked: allSelected } : {};
              if (option.id === -1) {
                return (
                  <li
                    {...props}
                    onClick={handleLoadMore}
                    style={{ cursor: "pointer" }}
                  >
                    🔄 Load More
                  </li>
                );
              }
              return (
                <li
                  {...props}
                  key={
                    generateCustomKey
                      ? generateCustomKey(option)
                      : option.id || option.name
                  }
                >
                  {multiple ? (
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                      {...selectAllProps}
                    />
                  ) : null}
                  {getOptionLabel(option)}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t(placeholder)}
                label={t(inputLabel)}
                error={!!errorInfo?.() && !disabled}
                helperText={
                  !disabled && !!errorInfo()
                    ? errorInfo()?.message?.toString()
                    : null
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

export default BasicAutocomplete;
